import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code2, CheckCircle, Users, Zap, LogIn } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import AuthButton from '@/auth/components/AuthButton';

export default function Landing() {
  // Un único useAuth() al principio: llamarlo de nuevo más abajo rompía la regla de los
  // hooks, porque quedaba después de los early returns.
  const { isAuthenticated, isLoading, login: ctaLogin } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to the code editor
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/submit');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando aplicación...</p>
        </div>
      </div>
    );
  }

  // If authenticated, show loading while redirecting
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Redirigiendo al editor...</p>
        </div>
      </div>
    );
  }

  const CtaLoginButton = () => (
    <button
      onClick={ctaLogin}
      className="bg-white hover:bg-gray-50 text-primary-600 font-semibold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 flex items-center gap-3 mx-auto"
    >
      <LogIn className="w-5 h-5" />
      Iniciar Sesión
    </button>
  );

  const features = [
    {
      icon: Code2,
      title: 'Evaluación Automática',
      description: 'Envía tu código en C y recibe feedback inmediato con resultados detallados.',
    },
    {
      icon: CheckCircle,
      title: 'Tests Completos',
      description: 'Cada ejercicio incluye casos de prueba exhaustivos para validar tu solución.',
    },
    {
      icon: Users,
      title: 'Seguimiento de Progreso',
      description: 'Monitorea tu progreso y revisa tus envíos anteriores en cualquier momento.',
    },
    {
      icon: Zap,
      title: 'Resultados Instantáneos',
      description: 'Obtén resultados en segundos con información detallada sobre rendimiento.',
    },
  ];

  const steps = [
    {
      number: 1,
      title: 'Inicia Sesión',
      description: 'Usa tu cuenta de Auth0 para acceder al sistema de forma segura.',
    },
    {
      number: 2,
      title: 'Envía tu Código',
      description: 'Escribe tu solución en C y envíala especificando el ejercicio y guía.',
    },
    {
      number: 3,
      title: 'Recibe Feedback',
      description: 'Obtén resultados instantáneos con detalles de cada test y sugerencias.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left Content */}
            <motion.div
              className="flex-1 text-center lg:text-left"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Logo + Title */}
              <div className="mb-8">
                <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Code2 className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    Leet PI
                  </h1>
                </div>

                <h2 className="text-2xl lg:text-3xl font-semibold text-gray-800 mb-4">
                  Plataforma de Evaluación de Código
                </h2>

                <p className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  Sistema de evaluación automática para ejercicios de programación en C.
                  Envía tu código y recibe feedback instantáneo con resultados detallados.
                </p>
              </div>

              {/* Auth Button */}
              <motion.div
                className="mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <AuthButton
                  className="bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 text-white font-semibold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 flex items-center gap-3 mx-auto lg:mx-0"
                />
              </motion.div>

              {/* Features Preview */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                {features.slice(0, 2).map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white/70 backdrop-blur-sm p-4 rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {feature.title}
                          </h3>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            </motion.div>

            {/* Right Content - Programming Illustration */}
            <motion.div
              className="flex-1 flex justify-center"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                {/* Code Editor Mockup */}
                <div className="bg-gray-900 rounded-2xl shadow-2xl p-6 max-w-md w-full">
                  {/* Editor Header */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Code2 className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-400 text-sm font-mono">main.c</span>
                    </div>
                  </div>

                  {/* Code Content */}
                  <div className="font-mono text-sm text-green-400 space-y-1">
                    <div className="text-blue-400">#include &lt;stdio.h&gt;</div>
                    <div className="text-blue-400">#include &lt;stdlib.h&gt;</div>
                    <br />
                    <div className="text-yellow-400">int main() {'{'}</div>
                    <div className="text-gray-300 ml-4">printf("Hello, World!");</div>
                    <div className="text-gray-300 ml-4">return 0;</div>
                    <div className="text-yellow-400">{'}'}</div>
                  </div>

                  {/* Status Bar */}
                  <div className="mt-4 pt-4 border-t border-gray-700 flex justify-between items-center text-xs text-gray-500">
                    <span>Línea 5, Columna 1</span>
                    <span className="text-green-400">✓ Sin errores</span>
                  </div>
                </div>

                {/* Floating Elements */}
                <motion.div
                  className="absolute -top-4 -right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg"
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  ✓ Aprobado
                </motion.div>

                <motion.div
                  className="absolute -bottom-4 -left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg"
                  animate={{
                    y: [0, -10, 0],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  ⚡ Rápido
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Características Principales
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Todo lo que necesitas para mejorar tus habilidades de programación
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-white/70 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-white/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              ¿Cómo Funciona?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Proceso simple en 3 pasos para comenzar a programar
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="text-center relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary-300 to-transparent"></div>
                )}

                <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg relative z-10">
                  <span className="text-white font-bold text-xl">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              ¿Listo para comenzar?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Únete a la plataforma y comienza a mejorar tus habilidades de programación hoy mismo
            </p>
            <CtaLoginButton />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-6 font-semibold">
            Créditos
          </p>
          <div className="flex flex-col gap-3">
            <span className="text-base text-gray-300">Martín Alejandro Barnatán — mbarnatan@itba.edu.ar</span>
            <span className="text-base text-gray-300">Manuel Cortés Teyssier — mcortesteyssier@itba.edu.ar</span>
            <span className="text-base text-gray-300">Ian Cruz Oniszczuk — ioniszczuk@itba.edu.ar</span>
            <span className="text-base text-gray-300">Adrián Etchevarne — aetcheva@itba.edu.ar</span>
          </div>
          <div className="mt-8 border-t border-gray-800 pt-6">
            <p className="text-sm text-gray-500">72.31 Programación Imperativa - Instituto Tecnológico de Buenos Aires</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
